<template>
  <div class="rpw-view">
    <div
      v-if="isLoading"
      class="loading"
    >
      <unc-loader />
    </div>

    <div
      v-else
      class="register-card rpw-card"
    >
      <div class="register-glow" />

      <div class="card-header ch-alt register-header">
        <div class="register-kicker rr-kicker--info">
          Recuperación de contraseña
        </div>
        <h1 class="register-title">
          Recuperar contraseña
        </h1>
        <p class="register-subtitle">
          Ingresá tu Usuario UNC para iniciar el proceso. Recordá añadir @unc.edu.ar o @mi.unc.edu.ar.
        </p>
      </div>

      <div class="card-body register-body">
        <form @submit.prevent="validateBeforeSubmit">
          <div
            class="form-group register-field"
            :class="{ 'has-error': errors.has('item.username') }"
          >
            <div>
              <label for="item.username">Usuario UNC</label>
              <input
                id="item.username"
                v-model="item.username"
                v-validate="'required|uncUserName|noSpaces|max:70'"
                name="item.username"
                type="text"
                autocomplete="username"
                class="form-control"
                placeholder="Ingresá tu Usuario UNC..."
                :aria-describedby="errors.has('item.username') ? 'item-username-error' : null"
                @keydown.space.prevent
                @input="item.username = $event.target.value.replace(/\s/g, '')"
              >
            </div>
            <small
              v-if="errors.has('item.username')"
              id="item-username-error"
              class="help-block"
              role="alert"
            >{{ errors.first('item.username') }}</small>
          </div>

          <div class="rpw-actions">
            <button
              type="submit"
              class="btn btn-primary waves-effect register-btn register-btn-primary"
            >
              <span>Recuperar contraseña</span>
              <svg
                aria-hidden="true"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="5"
                  y1="12"
                  x2="19"
                  y2="12"
                />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </button>
          </div>
        </form>
      </div>
    </div>

    <EmailHelpModal
      ref="emailHelpComponent"
      @closed="$router.push('/')"
    />
  </div>
</template>

<script>
import {
    HttpFRONT,
    swal,
} from '../../share.js';
import EmailHelpModal from '../utils/EmailHelpModal.vue';
import validateForm from '../../mixins/validate-form';

export default {
    name: 'RestorePassword',
    components: {
        EmailHelpModal,
    },
    mixins: [validateForm],
    data () {
        return {
            isLoading: false,
            item: {
                username: '',
            },
        };
    },
    methods: {
        openEmailHelp () {
            this.$refs.emailHelpComponent.open();
        },
        async validateBeforeSubmit () {
            try {
                if (await this.validateForm()) {
                    await this.recover();
                }
            } catch {
                swal({
                    title: 'Algo salió mal',
                    text: 'No pudimos recuperar tu contraseña.',
                    type: 'warning',
                });
            }
        },
        async recover () {
            this.isLoading = true;

            const dataToSend = {
                username: this.item.username.trim(),
            };

            try {
                const response = await HttpFRONT.post('usuarios/recuperar-clave-token-v2', dataToSend);

                if (response.status === 200) {
                    try {
                        await swal({
                            title: 'Solicitud recibida',
                            html: 'Si el Usuario UNC existe, enviamos un correo a la dirección de recuperación asociada.<br>Si no la recordás, comunicate con el administrador de tu dependencia o facultad.',
                            type: 'info',
                            showCancelButton: true,
                            confirmButtonText: 'Entendido (5s)',
                            cancelButtonText: 'No recibí el correo',
                            cancelButtonColor: '#aaa',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                            onOpen: () => {
                                swal.disableConfirmButton();
                                const confirmButton = swal.getConfirmButton();

                                if (confirmButton) {
                                    confirmButton.style.opacity = '0.55';
                                    confirmButton.style.cursor = 'not-allowed';
                                }

                                let timeLeft = 5;

                                const interval = setInterval(() => {
                                    timeLeft -= 1;
                                    if (confirmButton) {
                                        confirmButton.textContent = `Entendido (${timeLeft}s)`;
                                    }

                                    if (timeLeft <= 0) {
                                        clearInterval(interval);
                                        if (confirmButton) {
                                            confirmButton.textContent = 'Entendido';
                                            confirmButton.style.opacity = '1';
                                            confirmButton.style.cursor = 'pointer';
                                        }
                                        swal.enableConfirmButton();
                                    }
                                }, 1000);
                            },
                        });
                        this.$router.push('/');
                    } catch (dismiss) {
                        if (dismiss === 'cancel' && this.$refs.emailHelpComponent) {
                            this.$refs.emailHelpComponent.open();
                        }
                    }
                } else {
                    swal({
                        title: 'Algo salió mal',
                        text: 'Servicio temporalmente no disponible.',
                        type: 'warning',
                    });
                }
            } catch (error) {
                swal({
                    title: 'Algo salió mal',
                    text: 'Servicio temporalmente no disponible.',
                    type: 'warning',
                });
            } finally {
                this.isLoading = false;
            }
        },
        goBack () {
            this.$router.push('/');
        },
    },
};
</script>

<style lang="scss" scoped>
@import './register-shared.scss';

.rpw-view {
  --register-azul: #003865;
  --register-azul-deep: #001f3f;
  --register-azul-mid: #0a5f96;
  --register-verde: #3eb6b8;
  --register-ink: #102238;
  --register-muted: #526579;
  --register-border: rgba(0, 56, 101, 0.14);

  font-family: var(--font-body);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px 60px;
}

.rpw-card {
  width: 100%;
  max-width: $register-card-wide;
}

.register-field {
  margin-bottom: 18px;
}

.rpw-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

@media (max-width: 600px) {
  .rpw-view {
    padding: 24px 12px 40px;
  }

  .rpw-actions {
    justify-content: stretch;
    flex-direction: column-reverse;
    gap: 8px;
  }

  .rpw-actions .register-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
